import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLanguages } from '../../hooks/useLanguages';
import { loadVocabularyCatalog, loadConsolidatedVocabularyTopic } from '../../services/vocabularyV2';
import { GameBanner, SkeletonGrid } from '../../components/GameUI';

// Word games hub — mirrors the mobile app's GamesScreen: pick a vocabulary
// topic here, then launch whichever mini-game has enough data for it.
// Each game screen receives its word list via router state, built fresh
// from whatever topic/tab is selected below.

function wordFor(content, key) {
  const entry = content?.[key];
  if (entry && typeof entry === 'object') return entry.text == null ? '—' : String(entry.text);
  return entry == null ? '—' : String(entry);
}

function emojiFor(nativeContent, learnContent, key) {
  for (const content of [learnContent, nativeContent]) {
    const entry = content?.[key];
    if (entry && typeof entry === 'object' && entry.emoji) return String(entry.emoji);
  }
  return '';
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function GamesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { languagePrefs } = useAuth();
  const { languages } = useLanguages();
  const langMap = useMemo(() => Object.fromEntries(languages.map((l) => [l.code, l])), [languages]);

  const nativeCode = languagePrefs?.nativeLanguage || '';
  const learnCode = languagePrefs?.targetLanguage || '';
  const nativeLang = langMap[nativeCode] || { code: nativeCode, label: nativeCode, dir: 'ltr' };
  const targetLang = langMap[learnCode] || { code: learnCode, label: learnCode, dir: 'ltr' };
  const langPair = `${nativeCode}_${learnCode}`;

  const [thematic, setThematic] = useState([]);
  const [wordclass, setWordclass] = useState([]);
  const [indexLoaded, setIndexLoaded] = useState(false);
  const [indexError, setIndexError] = useState(false);
  const [activeTab, setActiveTab] = useState('thematic');
  const [category, setCategory] = useState(null);

  const [nativeContent, setNativeContent] = useState(null);
  const [learnContent, setLearnContent] = useState(null);
  const [vocabLoading, setVocabLoading] = useState(false);
  const [vocabError, setVocabError] = useState(false);

  useEffect(() => {
    if (!learnCode) return;
    let active = true;
    setIndexLoaded(false);
    setIndexError(false);
    loadVocabularyCatalog(learnCode)
      .then((raw) => {
        if (!active) return;
        const t2 = raw.thematic || [];
        const w2 = raw.wordclass || [];
        setThematic(t2);
        setWordclass(w2);
        setIndexLoaded(true);
        const cats = activeTab === 'thematic' ? t2 : w2;
        setCategory(cats[0]?.id ?? null);
      })
      .catch(() => {
        if (active) setIndexError(true);
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learnCode]);

  const currentCats = activeTab === 'thematic' ? thematic : wordclass;

  useEffect(() => {
    if (!category || !nativeCode || !learnCode) return;
    let active = true;
    setVocabLoading(true);
    setVocabError(false);
    setNativeContent(null);
    setLearnContent(null);
    loadConsolidatedVocabularyTopic(activeTab, learnCode, nativeCode, learnCode, category)
      .then((pack) => {
        if (!active) return;
        setNativeContent(pack.reader?.content || null);
        setLearnContent(pack.target?.content || null);
        setVocabLoading(false);
        setVocabError(!pack.reader?.content || !pack.target?.content);
      })
      .catch(() => {
        if (!active) return;
        setVocabError(true);
        setVocabLoading(false);
      });
    return () => { active = false; };
  }, [category, nativeCode, learnCode, activeTab]);

  function switchTab(tab) {
    if (tab === activeTab) return;
    setActiveTab(tab);
    const cats = tab === 'thematic' ? thematic : wordclass;
    setCategory(cats[0]?.id ?? null);
  }

  const sortedKeys = useMemo(() => (
    Array.from(new Set([...Object.keys(nativeContent || {}), ...Object.keys(learnContent || {})]))
  ), [nativeContent, learnContent]);

  const emojiKeys = useMemo(
    () => sortedKeys.filter((k) => emojiFor(nativeContent, learnContent, k)),
    [sortedKeys, nativeContent, learnContent],
  );

  const singleWordKeys = useMemo(() => sortedKeys.filter((k) => {
    const w = wordFor(learnContent, k);
    return !w.includes(' ') && w.length >= 3 && w.length <= 9;
  }), [sortedKeys, learnContent]);

  function playWordMatch() {
    const pairs = shuffle(sortedKeys).slice(0, 6).map((k) => ({
      id: k, native: wordFor(nativeContent, k), target: wordFor(learnContent, k),
    }));
    navigate('/games/word-match', { state: { pairs, nativeLang, targetLang, langPair } });
  }

  function playMemoryCards() {
    const pairs = shuffle(emojiKeys).slice(0, 6).map((k) => ({
      id: k, word: wordFor(learnContent, k), emoji: emojiFor(nativeContent, learnContent, k),
    }));
    navigate('/games/memory-cards', { state: { pairs, targetLang, langPair } });
  }

  function playBalloonPop() {
    const pairs = shuffle(sortedKeys).slice(0, 10).map((k) => ({
      id: k, native: wordFor(nativeContent, k), target: wordFor(learnContent, k),
    }));
    navigate('/games/balloon-pop', { state: { pairs, nativeLang, targetLang, langPair } });
  }

  function playWordSearch() {
    const words = shuffle(singleWordKeys).slice(0, 8).map((k) => wordFor(learnContent, k));
    navigate('/games/word-search', { state: { words, targetLang, langPair } });
  }

  function playHangman() {
    const words = shuffle(singleWordKeys).slice(0, 8).map((k) => wordFor(learnContent, k));
    navigate('/games/hangman', { state: { words, targetLang, langPair } });
  }

  async function playFruitSlice() {
    try {
      const [nounPack, verbPack, adjPack, advPack] = await Promise.all(
        ['nouns', 'verbs', 'adjectives', 'adverbs'].map((topic) =>
          loadConsolidatedVocabularyTopic('wordclass', learnCode, learnCode, learnCode, topic).catch(() => null)),
      );
      const wordsFrom = (pack, isNoun) => {
        const content = pack?.target?.content || {};
        return Object.keys(content)
          .map((k) => ({ id: k, word: wordFor(content, k), isNoun }))
          .filter((w) => w.word && w.word !== '—');
      };
      const nouns = wordsFrom(nounPack, true);
      if (nouns.length < 4) return;
      const others = [...wordsFrom(verbPack, false), ...wordsFrom(adjPack, false), ...wordsFrom(advPack, false)];
      navigate('/games/fruit-slice', { state: { nouns, others, targetLang, langPair } });
    } catch {
      // No noun data for this language yet — same as any other game with insufficient data.
    }
  }

  function playSpeedQuiz() {
    const pairs = shuffle(sortedKeys).slice(0, 12).map((k) => ({
      id: k, native: wordFor(nativeContent, k), target: wordFor(learnContent, k),
    }));
    navigate('/games/speed-quiz', { state: { pairs, nativeLang, targetLang, langPair } });
  }

  const ready = !vocabLoading && !vocabError;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <div className="mb-5 flex items-center justify-between">
          <h1 className="text-2xl font-black text-slate-900">{t('games.title')}</h1>
          <span className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-500 shadow-sm">
            {nativeLang.label} → {targetLang.label}
          </span>
        </div>

        {indexError ? (
          <p className="py-10 text-center text-sm text-slate-500">{t('games.failedToLoad')}</p>
        ) : !indexLoaded ? (
          <SkeletonGrid />
        ) : (
          <>
            {/* Boss Battle builds its own mixed vocab/listening/grammar
                question set on demand — not tied to the topic picker below,
                so it launches directly regardless of category/tab. */}
            <button
              type="button"
              onClick={() => navigate('/games/boss-battle')}
              className="mb-6 flex w-full items-center gap-3 rounded-2xl px-4 py-4 text-left shadow-lg shadow-red-400/30 transition hover:-translate-y-0.5 active:translate-y-0"
              style={{ background: 'linear-gradient(135deg, #7c2d12 0%, #dc2626 100%)' }}
            >
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/20 text-2xl">🐉</span>
              <span className="min-w-0 flex-1">
                <span className="block text-base font-black text-white">{t('games.bossBattle')}</span>
                <span className="block text-xs text-red-100">{t('games.bossBattleDesc')}</span>
              </span>
              <span className="shrink-0 text-white/80" aria-hidden="true">›</span>
            </button>

            <div className="mb-3 flex gap-2 overflow-x-auto pb-0.5 scrollbar-none">
              {[
                { id: 'thematic', label: t('practice.thematic'), icon: '📚' },
                { id: 'wordclass', label: t('practice.wordClass'), icon: '🔤' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => switchTab(tab.id)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-xs font-black transition-all ${
                    activeTab === tab.id
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-400/30'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300 shadow-sm'
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            <div className="mb-6 flex gap-2 overflow-x-auto pb-0.5 scrollbar-none">
              {currentCats.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setCategory(cat.id)}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-black transition-all ${
                    category === cat.id
                      ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-400/30'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300 shadow-sm'
                  }`}
                >
                  <span className="text-base leading-none">{cat.icon}</span>
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>

            <p className="mb-3 text-xs font-semibold text-slate-400">{t('games.chooseTopic')}</p>

            {vocabLoading ? (
              <SkeletonGrid rows={3} />
            ) : vocabError && !nativeContent && !learnContent ? (
              <p className="py-10 text-center text-sm text-slate-500">{t('games.noVocabForLanguages')}</p>
            ) : (
              <div className="flex flex-col gap-3">
                {ready && sortedKeys.length >= 4 && (
                  <GameBanner icon="🎮" title={t('games.wordMatch')} desc={t('games.wordMatchDesc')} onClick={playWordMatch} />
                )}
                {ready && emojiKeys.length >= 4 && (
                  <GameBanner icon="🧠" title={t('games.memoryCards')} desc={t('games.memoryCardsDesc')} onClick={playMemoryCards} />
                )}
                {ready && sortedKeys.length >= 4 && (
                  <GameBanner icon="🎈" title={t('games.balloonPop')} desc={t('games.balloonPopDesc')} onClick={playBalloonPop} />
                )}
                {ready && singleWordKeys.length >= 4 && (
                  <GameBanner icon="🔎" title={t('games.wordSearch')} desc={t('games.wordSearchDesc')} onClick={playWordSearch} />
                )}
                {ready && singleWordKeys.length >= 6 && (
                  <GameBanner icon="🎩" title={t('games.hangman')} desc={t('games.hangmanDesc')} onClick={playHangman} />
                )}
                {ready && (
                  <GameBanner icon="🍉" title={t('games.fruitSlice')} desc={t('games.fruitSliceDesc')} onClick={playFruitSlice} />
                )}
                {ready && sortedKeys.length >= 4 && (
                  <GameBanner icon="⚡" title={t('games.speedQuiz')} desc={t('games.speedQuizDesc')} onClick={playSpeedQuiz} />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
